import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Room, Character } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import websocketService from '../../services/websocket';
import { MessageHandler } from '../../services/messageHandler';
import { KeywordBadge } from '../ui/KeywordBadge';
import { DamageTypeBadge } from '../ui/DamageTypeBadge';

const RoomLobby: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const autoJoin = searchParams.get('autoJoin') === 'true';
  
  const [room, setRoom] = useState<Room | null>(null);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasJoined, setHasJoined] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  // Interval id lives in a ref: the WebSocket message handler is created once
  // on mount, so state captured in its closures would always be stale.
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearCountdownInterval = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  // Scroll countdown modal into view when it appears
  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      // Small delay to ensure the modal is rendered
      setTimeout(() => {
        const modal = document.querySelector('.countdown-modal');
        if (modal) {
          modal.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [countdown]);

  // Format range for display
  const formatRange = (range: { min: number; max: number; text?: string }) => {
    // If we have the original range text from lwdoc, use that as primary display
    if (range.text) {
      return range.text;
    }
    
    // Fallback to the computed range format
    if (range.min === range.max) {
      return `${range.min} pace${range.min !== 1 ? 's' : ''}`;
    }
    return `${range.min}-${range.max} paces`;
  };

  const formatRangeValues = (range: { min: number; max: number }) => {
    const parts = [];
    
    // Only show minimum if it's greater than 0
    if (range.min > 0) {
      parts.push(`Min Range: ${range.min}`);
    }
    
    return parts.join(' • ');
  };
  const [showAllCharacters, setShowAllCharacters] = useState(false);
  const [selectingCharacter, setSelectingCharacter] = useState(false);

  useEffect(() => {
    if (roomId && user && !hasJoined) {
      // Set up WebSocket connection for real-time updates
      const messageHandler = new MessageHandler({
        onPlayerJoined: ({ room }) => {
          console.log('Player joined - updating room:', room);
          setRoom(room);
        },
        onPlayerLeft: ({ room }) => {
          console.log('Player left - updating room:', room);
          setRoom(room);
        },
        onPlayerReady: ({ room, gameStarted, countdownCancelled }) => {
          console.log('Player ready - updating room:', room);
          setRoom(room);

          // Handle countdown cancellation
          if (countdownCancelled) {
            console.log('Countdown cancelled via player_ready message');
            setCountdown(null);
            clearCountdownInterval();
          }

          if (gameStarted) {
            navigate(`/rooms/${roomId}/game`);
          }
        },
        onCharacterSelected: ({ room }) => {
          console.log('Character selected - updating room:', room);
          setRoom(room);
        },
        onGameStarted: () => {
          console.log('Game started - navigating to game');
          navigate(`/rooms/${roomId}/game`);
        },
        onCountdownStarted: ({ remainingSeconds }) => {
          console.log('Countdown started:', remainingSeconds);
          setCountdown(remainingSeconds);

          // Clear any existing countdown interval
          clearCountdownInterval();

          // Start countdown timer
          const intervalId = setInterval(() => {
            setCountdown((prev) => {
              if (prev === null) {
                clearCountdownInterval();
                return null;
              }
              if (prev <= 1) {
                clearCountdownInterval();
                // Countdown reached 0, trigger game start
                handleCountdownComplete();
                return null;
              }
              return prev - 1;
            });
          }, 1000);

          countdownIntervalRef.current = intervalId;
        },
        onCountdownCancelled: () => {
          console.log('Countdown cancelled');
          setCountdown(null);
          clearCountdownInterval();
        }
      });

      websocketService.addMessageHandler(messageHandler.handleMessage);
      
      // Connect WebSocket first
      websocketService.connect(roomId, user.id);
      
      // If autoJoin is true, join the room after WebSocket is connected
      if (autoJoin) {
        // Small delay to ensure WebSocket connection is established
        setTimeout(async () => {
          try {
            await api.post(`/rooms/${roomId}/join`, {});
            setHasJoined(true);
            // Remove autoJoin parameter from URL
            navigate(`/rooms/${roomId}/lobby`, { replace: true });
          } catch (err: any) {
            if (err.response?.status === 404) {
              setError('Room not found');
            } else if (err.response?.status === 400) {
              // User might already be in the room
              const errorMsg = err.response.data?.error || err.response.data?.message || '';
              if (errorMsg.includes('already in this room')) {
                setHasJoined(true);
                navigate(`/rooms/${roomId}/lobby`, { replace: true });
              } else {
                setError(errorMsg || 'Cannot join room');
              }
            } else {
              setError('Failed to join room');
            }
          }
        }, 500);
      } else {
        setHasJoined(true);
      }
      
      // Fetch initial data
      fetchRoomData();
      fetchCharacters();

      return () => {
        websocketService.removeMessageHandler(messageHandler.handleMessage);
        // Don't disconnect WebSocket here - let GamePage reuse the connection

        // Clear countdown interval on unmount
        clearCountdownInterval();
      };
    }
  }, [roomId, user]);

  const fetchRoomData = async () => {
    try {
      const response = await api.get(`/rooms/${roomId}`);
      setRoom(response.data);
      
      // Find current user's state in the room
      const currentPlayer = response.data.players.find(
        (p: any) => p.userId === user?.id
      );
      if (currentPlayer) {
        setSelectedCharacter(currentPlayer.character);
        setIsReady(currentPlayer.isReady);
      }
      
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Room not found');
      } else {
        setError('Failed to load room data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCharacters = async () => {
    try {
      console.log('Fetching characters...');
      const response = await api.get('/characters');
      console.log('Characters response:', response.data);
      const charactersData = response.data.characters || [];
      console.log('Setting characters:', charactersData.length, 'characters');
      setCharacters(charactersData);
    } catch (err) {
      console.error('Failed to load characters:', err);
    }
  };

  const handleCharacterSelect = async (character: Character) => {
    if (isReady || selectingCharacter) return; // Can't change character when ready or already selecting

    try {
      setSelectingCharacter(true);
      console.log('Selecting character:', character.name, character.characterId);
      
      // Add a minimum loading time for better UX (prevents flash for fast responses)
      await Promise.all([
        api.post(`/rooms/${roomId}/select-character`, {
          characterId: character.characterId,
        }),
        new Promise(resolve => setTimeout(resolve, 500)) // Minimum 500ms loading
      ]);
      
      setSelectedCharacter(character);
      setError(null);
    } catch (err: any) {
      console.error('Failed to select character:', err);
      setError('Failed to select character');
    } finally {
      setSelectingCharacter(false);
    }
  };

  const filteredCharacters = characters.filter(character =>
    character.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    character.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedCharacters = showAllCharacters 
    ? filteredCharacters 
    : filteredCharacters.slice(0, 12);

  const handleReadyToggle = async () => {
    if (!selectedCharacter) {
      setError('Please select a character first');
      return;
    }

    try {
      await api.post(`/rooms/${roomId}/ready`, {
        isReady: !isReady,
      });
      setIsReady(!isReady);
      setError(null);
    } catch (err: any) {
      setError('Failed to update ready status');
    }
  };

  const handleLeaveRoom = async () => {
    try {
      await api.post(`/rooms/${roomId}/leave`);
    } catch (err: any) {
      console.error('Failed to leave room:', err);
      // Still navigate away even if the API call fails
    } finally {
      // Close the room's WebSocket connection so we stop receiving updates
      websocketService.disconnect();
      navigate('/');
    }
  };

  const handleCountdownComplete = async () => {
    console.log('Countdown complete, starting game...');
    try {
      // Trigger game start
      await api.post(`/rooms/${roomId}/start`);
      // Navigation will happen via WebSocket message
    } catch (err: any) {
      console.error('Failed to start game:', err);
      // Check if the error is because someone became not ready
      if (err.response?.status === 400) {
        const errorMsg = err.response.data?.error || err.response.data?.message || '';
        if (errorMsg.includes('already started')) {
          // Another client won the race to start the game — the game_started
          // broadcast (or this fallback) takes us to the game screen
          navigate(`/rooms/${roomId}/game`);
        } else if (errorMsg.includes('ready')) {
          setError('Game start cancelled - not all players are ready');
          setCountdown(null);
        } else {
          setError('Failed to start game: ' + errorMsg);
        }
      } else {
        setError('Failed to start game');
      }
    }
  };

  if (loading) {
    return (
      <div className="room-lobby-container">
        <div className="loading">Loading room...</div>
      </div>
    );
  }

  if (error && !room) {
    return (
      <div className="room-lobby-container">
        <div className="error-message">
          {error}
          <button onClick={handleLeaveRoom}>Back to Rooms</button>
        </div>
      </div>
    );
  }

  return (
    <div className="room-lobby-container">
      <div className="lobby-header">
        <h1>Room {roomId?.slice(0, 8)}</h1>
        <div className="lobby-actions">
          <button onClick={handleLeaveRoom} className="leave-button">
            Leave Room
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="lobby-content">
        <div className="players-section">
          <h2>Players ({room?.players.length}/{room?.maxPlayers})</h2>
          <div className="players-list">
            {room?.players.map((player, index) => (
              <div key={index} className="player-item">
                <div className="player-info">
                  <span className="player-name">{player.username}</span>
                  {player.character && (
                    <span className="player-character">
                      {player.character.name}
                    </span>
                  )}
                </div>
                <div className="player-status">
                  <span className={`ready-status ${player.isReady ? 'ready' : 'not-ready'}`}>
                    {player.isReady ? '✓ Ready' : 'Not Ready'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="character-section">
          <h2>Character Selection</h2>
          
          {selectedCharacter ? (
            <div className="selected-character">
              <div className="selected-character-header">
                <div>
                  <h3>{selectedCharacter.name}</h3>
                  <p className="character-type">{selectedCharacter.type}</p>
                </div>
                {!isReady && (
                  <button
                    className="change-character-button"
                    onClick={() => {
                      setSelectedCharacter(null);
                      setIsReady(false); // Ensure ready status is reset
                      setSearchTerm(''); // Clear search when changing character
                    }}
                    type="button"
                  >
                    Change Character
                  </button>
                )}
              </div>
              
              <div className="character-stats">
                <h4>Base Stats:</h4>
                <div className="stats-grid">
                  <div>Health: {selectedCharacter.baseStats.health}</div>
                  <div>Will: {selectedCharacter.baseStats.will}</div>
                  <div>Attack: +{selectedCharacter.baseStats.attackBonus}</div>
                  <div>Defense: +{selectedCharacter.baseStats.defenseBonus}</div>
                  <div>Speed: {selectedCharacter.baseStats.speed}</div>
                </div>
              </div>

              {selectedCharacter.keywords.length > 0 && (
                <div className="character-keywords">
                  <h4>Keywords:</h4>
                  <div className="keywords-list">
                    {selectedCharacter.keywords.map((keyword, index) => (
                      <KeywordBadge
                        key={index}
                        keyword={keyword.name}
                        value={keyword.value}
                        showTooltip={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="character-abilities">
                <h4>Abilities ({selectedCharacter.abilities.length}):</h4>
                <div className="abilities-list">
                  {selectedCharacter.abilities.map((ability, index) => (
                    <div key={index} className="ability-item">
                      <div className="ability-header">
                        <strong>{ability.name}</strong>
                        <div className="ability-stats">
                          {ability.damage > 0 && (
                            <span className="damage-stat">Damage: {ability.damage}</span>
                          )}
                          {ability.damageType && (
                            <DamageTypeBadge
                              damageType={ability.damageType}
                              showTooltip={true}
                            />
                          )}
                          <span className="range-stat">Range: {formatRange(ability.range)}</span>
                        </div>
                      </div>
                      <div className="ability-details">
                        <p className="ability-description">{ability.description}</p>
                        {ability.maxUses !== undefined && ability.maxUses !== null && (
                          <p className="text-sm" style={{ color: '#f4d03f', marginTop: '4px', marginBottom: '4px' }}>
                            Max Uses: {ability.maxUses}
                          </p>
                        )}
                        <div className="range-values">{formatRangeValues(ability.range)}</div>
                      </div>
                      
                      {/* Display sub-abilities if they exist */}
                      {ability.subAbilities && ability.subAbilities.length > 0 && (
                        <div className="sub-abilities">
                          <div className="sub-abilities-header">Sub-abilities:</div>
                          {ability.subAbilities.map((subAbility, subIndex) => (
                            <div key={subIndex} className="sub-ability-item">
                              <div className="sub-ability-header">
                                <strong>{subAbility.name}</strong>
                                <div>
                                  {subAbility.damage > 0 && (
                                    <span className="sub-ability-damage">Dmg: {subAbility.damage}</span>
                                  )}
                                  {subAbility.damageType && (
                                    <DamageTypeBadge
                                      damageType={subAbility.damageType}
                                      showTooltip={true}
                                      className="text-xs"
                                    />
                                  )}
                                  <span className="sub-ability-range">Rng: {formatRange(subAbility.range)}</span>
                                </div>
                              </div>
                              <p className="sub-ability-description">{subAbility.description}</p>
                              {subAbility.maxUses !== undefined && subAbility.maxUses !== null && (
                                <p className="text-xs" style={{ color: '#f4d03f', marginTop: '2px' }}>
                                  Max Uses: {subAbility.maxUses}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="character-selection">
              <p>Select a character to play:</p>
              
              {/* Search Input */}
              <div className="character-search">
                <input
                  type="text"
                  placeholder="Search characters by name or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  disabled={selectingCharacter}
                />
              </div>

              {/* Characters Grid with Scrolling */}
              <div className="characters-container">
                {selectingCharacter && (
                  <div className="character-selection-overlay">
                    <div className="character-selection-loading">
                      <div className="loading-spinner"></div>
                      <p>Selecting character...</p>
                    </div>
                  </div>
                )}
                <div className="characters-grid">
                  {displayedCharacters.map((character) => (
                    <button
                      key={character.characterId}
                      className="character-option"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Character clicked:', character.name);
                        handleCharacterSelect(character);
                      }}
                      disabled={isReady || selectingCharacter}
                      type="button"
                    >
                      <div className="character-name">{character.name}</div>
                      <div className="character-type">{character.type}</div>
                      <div className="character-stats-preview">
                        HP: {character.baseStats.health} | 
                        Will: {character.baseStats.will} | 
                        Spd: {character.baseStats.speed}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Show More/Less Button */}
              <div className="character-controls">
                {filteredCharacters.length > 12 && (
                  <button
                    className="show-more-button"
                    onClick={() => setShowAllCharacters(!showAllCharacters)}
                    type="button"
                  >
                    {showAllCharacters 
                      ? `Show Less (${displayedCharacters.length}/${filteredCharacters.length})` 
                      : `Show All Characters (${filteredCharacters.length})`
                    }
                  </button>
                )}
                
                {searchTerm && (
                  <p className="search-results">
                    Found {filteredCharacters.length} character{filteredCharacters.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="ready-section">
            <button
              className={`ready-button ${isReady ? 'ready' : 'not-ready'}`}
              onClick={handleReadyToggle}
              disabled={!selectedCharacter}
            >
              {isReady ? 'Not Ready' : 'Ready'}
            </button>
            
            {!selectedCharacter && (
              <p className="ready-hint">Select a character to mark yourself as ready</p>
            )}
          </div>
        </div>
      </div>

      {countdown !== null && countdown > 0 && (
        <div className="countdown-overlay">
          <div className="countdown-backdrop" onClick={handleReadyToggle}></div>
          <div className="countdown-modal">
            <h2>Game starting in {countdown} second{countdown !== 1 ? 's' : ''}...</h2>
            <p>Click "Not Ready" to cancel</p>
            <button
              className="countdown-cancel-button"
              onClick={handleReadyToggle}
            >
              Not Ready
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomLobby;